import React from "react";

import twMerge from "@replo/design-system/utils/twMerge";

import { Checkbox } from "../checkbox/Checkbox";
import Description from "../label/Description";
import Label from "../label/Label";

export type SelectableCardProps = {
  size: "base" | "sm";
  UNSAFE_className?: string;
  layoutClassName?: string;
  isActive?: boolean;
  startEnhancer?: React.ReactNode;
  title?: string;
  description?: string;
  variant?: "checkbox" | "default";
  onClick?: () => void;
  buttonClassName?: string;
  descriptionSize?: "xs" | "sm" | "base";
  contentClassName?: string;
};

const SelectableCard: React.FC<React.PropsWithChildren<SelectableCardProps>> = (
  props,
) => {
  const {
    size,
    UNSAFE_className,
    layoutClassName,
    isActive,
    startEnhancer,
    title,
    description,
    variant = "default",
    children,
    onClick,
    buttonClassName,
    contentClassName,
    descriptionSize,
  } = props;

  const isClickable = Boolean(onClick);
  const content = (
    <div
      className={twMerge("flex flex-1 flex-col gap-4 p-3", contentClassName)}
    >
      {startEnhancer && startEnhancer}
      {(title ?? description) && (
        <div className="flex w-full flex-row gap-2 break-words text-left">
          {variant === "checkbox" && <Checkbox checked={isActive} />}
          <div className="flex flex-col gap-[2px]">
            <Label
              label={title}
              size={size}
              breakWords
              UNSAFE_className={twMerge(
                size === "base" && "!leading-none",
                isClickable && "cursor-pointer",
              )}
            />
            <Description
              description={description}
              size={descriptionSize ?? size}
              breakWords
            />
          </div>
        </div>
      )}
      {children && (
        <div className="flex flex-1 items-center justify-center">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={twMerge(
        "border-0.5 border-border text-muted flex break-words rounded-lg text-left font-normal",
        isClickable && "cursor-pointer hover:bg-sheet",
        isClickable && !isActive && "hover:bg-selectable-hover",
        isActive &&
          "border-primary bg-selectable-selected hover:bg-selectable-selected-hover",
        layoutClassName,
        UNSAFE_className,
      )}
    >
      {isClickable ? (
        <button
          type="button"
          className={twMerge("flex-1 cursor-pointer", buttonClassName)}
          onClick={onClick}
        >
          {content}
        </button>
      ) : (
        content
      )}
    </div>
  );
};

export default SelectableCard;
